import type { Ref } from 'vue';

export function nyxMessageError(text: string, optional = false): string {
  if (new TextEncoder().encode(text).length > 16000)
    return 'Messages must be at most 16,000 UTF-8 bytes.';
  if (text.includes('\0')) return 'Messages cannot contain a null character.';
  return !optional && !text.trim() ? 'Enter a message.' : '';
}

/** Serialize whole operations so polling cannot interleave a person's requests. */
export function createNyxActions(options: {
  busy: Ref<boolean>;
  error: Ref<string>;
  active: () => boolean;
  settled: (background: boolean) => void;
}) {
  let inFlight: Promise<void> | null = null;
  return async (work: () => Promise<void>, background = false) => {
    if (!options.active() || options.busy.value || (background && inFlight)) return;
    if (!background) {
      options.busy.value = true;
      options.error.value = '';
    }
    while (inFlight) await inFlight;
    const job = Promise.resolve().then(async () => {
      if (!options.active()) return;
      try {
        await work();
      } catch (raised) {
        options.error.value = raised instanceof Error ? raised.message : String(raised);
      }
    });
    inFlight = job;
    try {
      await job;
    } finally {
      if (inFlight === job) inFlight = null;
      if (!background) options.busy.value = false;
      if (options.active()) options.settled(background);
    }
  };
}

/** Bind the initial message to the task returned by this start request. */
export async function startNyxTask<T extends { id: string }>(options: {
  draft: Ref<string>;
  start: () => Promise<T>;
  send: (taskId: string, text: string) => Promise<unknown>;
  active: () => boolean;
  initialText?: string;
}): Promise<T> {
  const text = options.initialText ?? options.draft.value;
  const invalid = nyxMessageError(text, true);
  if (invalid) throw new Error(invalid);
  const task = await options.start();
  if (text.trim() && options.active()) {
    await options.send(task.id, text);
    if (options.draft.value === text) options.draft.value = '';
  }
  return task;
}
